"use client";

import { Slider } from "@/components/ui/slider";

import {
  formatAmountWithUnit,
  clampPercentForSlider,
  clampWholeForSlider,
  formatPercentLabel,
  formatPercentRaw,
  formatWholeNumber,
  parsePercent,
  parseWholeNumber,
  resolveSliderDragStep,
  snapPercentToSliderStep,
  snapWholeToSliderStep,
  type TrafficCalculatorSliderRange,
} from "./seo-revenue-projection";

export type CcTrafficSliderFieldProps = {
  id: string;
  field: string;
  label: string;
  raw: string;
  onChange: (next: string) => void;
  kind: "whole" | "percent";
  range: TrafficCalculatorSliderRange;
  locale?: string;
  /** Shown beside the compact input (e.g. "kr exkl. moms" or "%"). */
  unit?: string;
  help?: string;
  error?: string | null;
};

function formatBound(
  value: number,
  kind: "whole" | "percent",
  locale: string,
): string {
  if (kind === "whole") return formatWholeNumber(value, locale);
  return formatPercentLabel(value, locale);
}

export function CcTrafficSliderField({
  id,
  field,
  label,
  raw,
  onChange,
  kind,
  range,
  locale = "sv-SE",
  unit,
  help,
  error,
}: CcTrafficSliderFieldProps) {
  const numeric =
    kind === "whole"
      ? clampWholeForSlider(raw, range)
      : clampPercentForSlider(raw, range);
  const describedBy = [
    error ? `${id}-error` : null,
    help ? `${id}-help` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const valueText =
    kind === "whole"
      ? unit
        ? formatAmountWithUnit(numeric, unit, locale)
        : formatWholeNumber(numeric, locale)
      : formatPercentLabel(numeric, locale);

  const sliderStep = resolveSliderDragStep(range);

  const formatOnBlur = () => {
    if (kind === "whole") {
      const parsed = parseWholeNumber(raw);
      if (parsed !== null && !Number.isNaN(parsed)) {
        const clamped = Math.min(range.max, Math.max(range.min, parsed));
        const committed = snapWholeToSliderStep(clamped, range);
        onChange(formatWholeNumber(committed, locale));
      }
      return;
    }
    const parsed = parsePercent(raw);
    if (parsed !== null && !Number.isNaN(parsed)) {
      const clamped = Math.min(range.max, Math.max(range.min, parsed));
      onChange(formatPercentRaw(snapPercentToSliderStep(clamped, range), locale));
    }
  };

  return (
    <div className={`cc-traffic__field cc-traffic__field--${field}`}>
      <label id={`${id}-label`} htmlFor={id} className="cc-traffic__label">
        {label}
      </label>
      <div className="cc-traffic__control cc-traffic__control--slider-field">
        <div className="cc-traffic__slider-input-row">
          <input
            id={id}
            className={`cc-traffic__input${kind === "percent" ? " cc-traffic__input--percent" : " cc-traffic__input--compact"}`}
            inputMode={kind === "percent" ? "decimal" : "numeric"}
            autoComplete="off"
            value={raw}
            onChange={(event) => onChange(event.target.value)}
            onBlur={formatOnBlur}
            aria-invalid={error !== null && error !== undefined}
            aria-describedby={describedBy || undefined}
          />
          {unit ? (
            <span className="cc-traffic__slider-inline-unit" aria-hidden="true">
              {unit}
            </span>
          ) : null}
        </div>
        <Slider
          className="cc-traffic__shadcn-slider"
          min={range.min}
          max={range.max}
          step={sliderStep}
          value={[numeric]}
          onValueChange={([next]) => {
            if (next === undefined) return;
            onChange(
              kind === "whole"
                ? formatWholeNumber(next, locale)
                : formatPercentRaw(next, locale),
            );
          }}
          onValueCommit={([next]) => {
            if (next === undefined) return;
            if (kind === "whole") {
              onChange(formatWholeNumber(snapWholeToSliderStep(next, range), locale));
              return;
            }
            onChange(formatPercentRaw(snapPercentToSliderStep(next, range), locale));
          }}
          aria-label={label}
          aria-invalid={error !== null && error !== undefined}
          aria-describedby={describedBy || undefined}
          aria-valuetext={valueText}
        />
        <div className="cc-traffic__slider-bounds" aria-hidden="true">
          <span>{formatBound(range.min, kind, locale)}</span>
          <span>{formatBound(range.max, kind, locale)}</span>
        </div>
      </div>
      {error ? (
        <p id={`${id}-error`} className="cc-traffic__help is-error">
          {error}
        </p>
      ) : help ? (
        <p id={`${id}-help`} className="cc-traffic__help">
          {help}
        </p>
      ) : null}
    </div>
  );
}
